import { useState, useEffect } from "react";
import { X, Upload, Link as LinkIcon, Check } from "lucide-react";
import { IconButton, Button } from "./ui/button";

interface ImageEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentImage: string;
  itemName: string;
  onSave: (imageUrl: string) => void;
}

export function ImageEditModal({
  isOpen,
  onClose,
  currentImage,
  itemName,
  onSave,
}: ImageEditModalProps) {
  const [imageUrl, setImageUrl] = useState(currentImage);
  const [previewUrl, setPreviewUrl] = useState(currentImage);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(previewUrl);
    onClose();
  };

  const handleUrlChange = (url: string) => {
    setImageUrl(url);
    setPreviewUrl(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-modal">
      <div className="bg-[var(--card)] rounded-xl shadow-xl border border-[var(--border)] w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--border)]">
          <div>
            <h2 className="text-xl font-semibold text-[var(--foreground)]">Modifier l'image</h2>
            <p className="text-sm text-[var(--muted-foreground)] mt-1">{itemName}</p>
          </div>
          <IconButton
            icon={X}
            variant="ghost"
            onClick={onClose}
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* URL Input */}
          <div>
            <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-2">
              <LinkIcon className="w-4 h-4 inline mr-2" />
              URL de l'image
            </label>
            <input
              type="text"
              value={imageUrl}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="w-full px-4 py-3 bg-[var(--muted)] border border-[var(--switch-background)] rounded-lg text-[var(--foreground)] placeholder-[var(--muted-foreground-light)] focus:outline-none focus:ring-2 focus:ring-[var(--color-steel-dark)] focus:border-transparent"
            />
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-2">
              Aperçu
            </label>
            <div className="relative w-full h-64 bg-[var(--muted)] rounded-lg overflow-hidden border border-[var(--switch-background)]">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Aperçu"
                  className="w-full h-full object-contain"
                  onError={() => setPreviewUrl("")}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-[var(--muted-foreground-light)]">
                  <div className="text-center">
                    <Upload className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Aucune image à afficher</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-[var(--border)]">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            Annuler
          </Button>
          <Button
            variant="primary"
            icon={Check}
            onClick={handleSave}
            disabled={!previewUrl}
          >
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}